/**
 * Cart Recommendations — "Tu pourrais aimer"
 *
 * Stratégie de chargement des recommandations (avec fallbacks) :
 *  1. API Shopify intent=related  → résultats algorithmiques
 *  2. API Shopify (sans intent)   → résultats génériques si related vide
 *  3. Produits de la collection   → fallback collection si tout le reste est vide
 */
(function () {
  'use strict';

  const formatPrice = (cents) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  let lastProductId = null;
  let initTimer     = null;
  let isFetching    = false;

  /* ─── Bootstrap ─────────────────────────────────────────────────────────── */

  function initRecommendations() {
    clearTimeout(initTimer);
    initTimer = setTimeout(_init, 200);
  }

  function _init() {
    if (isFetching) return;

    const reco = document.getElementById('cart-reco');
    if (!reco) return;

    const productId        = reco.dataset.productId;
    const collectionHandle = reco.dataset.collectionHandle || '';
    if (!productId) return;

    // Ne pas re-fetcher si le produit n'a pas changé
    if (productId === lastProductId) return;
    lastProductId = productId;

    const track = document.getElementById('cart-reco-track');
    if (!track) return;

    track.innerHTML = '<span class="cart-reco-loading"></span>';

    isFetching = true;
    fetchProducts(productId, collectionHandle)
      .then(function (products) {
        isFetching = false;
        if (!products || products.length === 0) {
          reco.style.display = 'none';
          return;
        }
        reco.style.display = '';
        renderProducts(track, products);
        initCarousel();
      })
      .catch(function (err) {
        isFetching = false;
        console.warn('[cart-reco] Erreur chargement :', err);
        reco.style.display = 'none';
      });
  }

  /* ─── Fetch avec fallbacks ───────────────────────────────────────────────── */

  function fetchProducts(productId, collectionHandle) {
    // Tentative 1 : recommandations liées (nécessite historique Shopify)
    return fetch('/recommendations/products.json?product_id=' + productId + '&limit=6&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.products && data.products.length >= 2) return data.products;

        // Tentative 2 : recommandations génériques (sans intent)
        return fetch('/recommendations/products.json?product_id=' + productId + '&limit=6')
          .then(function (r) { return r.json(); })
          .then(function (data2) {
            if (data2.products && data2.products.length >= 2) return data2.products;

            // Tentative 3 : produits de la même collection
            if (!collectionHandle) return [];
            return fetch('/collections/' + collectionHandle + '/products.json?limit=8&sort_by=best-selling')
              .then(function (r) { return r.json(); })
              .then(function (data3) {
                // Exclure le produit courant
                return (data3.products || [])
                  .filter(function (p) { return String(p.id) !== String(productId); })
                  .slice(0, 6);
              });
          });
      });
  }

  /* ─── Ratings via template alternatif (/products/{handle}?view=rating) ───── */

  function fetchRatings(products) {
    var handles = products.map(function (p) { return p.handle; }).filter(Boolean);
    if (!handles.length) return Promise.resolve({});

    // Fetch chaque produit via le template rating qui expose les metafields
    var requests = handles.map(function (handle) {
      return fetch('/products/' + handle + '?view=rating&sections=product-rating-data')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (sections) {
          if (!sections || !sections['product-rating-data']) return null;
          var div = document.createElement('div');
          div.innerHTML = sections['product-rating-data'];
          var script = div.querySelector('.product-rating-json');
          if (!script) return null;
          try { return JSON.parse(script.textContent); } catch (e) { return null; }
        })
        .catch(function () { return null; });
    });

    return Promise.all(requests).then(function (results) {
      var map = {};
      results.forEach(function (r) {
        if (r && r.id) map[String(r.id)] = r;
      });
      return map;
    });
  }

  function buildStars(rating, max) {
    if (!rating || !max) return '';
    var pct = Math.round((rating / max) * 100);
    return (
      '<div class="cart-reco-stars" aria-label="' + rating.toFixed(1) + ' sur ' + max + '">' +
        '<span class="cart-reco-stars__bg">★★★★★</span>' +
        '<span class="cart-reco-stars__fg" style="width:' + pct + '%">★★★★★</span>' +
      '</div>'
    );
  }

  /* ─── Rendu des cartes ───────────────────────────────────────────────────── */

  function renderProducts(track, products) {
    // D'abord rendu sans ratings, puis on enrichit
    track.innerHTML = products.map(function (p) {
      return buildCard(p, null);
    }).join('');

    track.querySelectorAll('.cart-reco-item__btn').forEach(function (btn) {
      btn.addEventListener('click', function () { addToCart(btn); });
    });

    // Chargement des ratings en arrière-plan
    fetchRatings(products).then(function (ratingsMap) {
      products.forEach(function (p) {
        var ratingData = ratingsMap[String(p.id)];
        if (!ratingData || !ratingData.rating || !ratingData.count) return;
        var card = track.querySelector('[data-product-id="' + p.id + '"]');
        if (!card) return;
        var starsSlot = card.querySelector('.cart-reco-stars-slot');
        if (starsSlot) {
          starsSlot.innerHTML =
            buildStars(ratingData.rating, ratingData.max) +
            '<span class="cart-reco-rating-count">(' + ratingData.count + ')</span>';
        }
      });
    });
  }

  function buildCard(p, ratingData) {
    var variant = p.variants && p.variants[0];
    if (!variant) return '';

    var img = '';
    if (p.featured_image) {
      img = p.featured_image.startsWith('//') ? 'https:' + p.featured_image : p.featured_image;
      if (img.includes('cdn.shopify.com') && !img.includes('?')) {
        img += '?width=300&height=300&crop=center';
      }
    }

    var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;

    return (
      '<div class="cart-reco-item" data-product-id="' + p.id + '">' +
        '<a href="' + p.url + '" class="cart-reco-item__img-link">' +
          (img
            ? '<img src="' + img + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy" width="150" height="150">'
            : '<div class="cart-reco-item__img-placeholder"></div>') +
        '</a>' +
        '<div class="cart-reco-item__info">' +
          '<a href="' + p.url + '" class="cart-reco-item__title">' + p.title + '</a>' +
          '<div class="cart-reco-stars-slot"></div>' +
          '<div class="cart-reco-item__prices">' +
            (hasCompare ? '<s class="cart-reco-item__compare">' + formatPrice(variant.compare_at_price) + '</s>' : '') +
            '<span class="cart-reco-item__price' + (hasCompare ? ' cart-reco-item__price--sale' : '') + '">' +
              formatPrice(variant.price) +
            '</span>' +
          '</div>' +
          (variant.available
            ? '<button class="cart-reco-item__btn" data-variant-id="' + variant.id + '">+ Ajouter</button>'
            : '<span class="cart-reco-item__unavailable">Épuisé</span>') +
        '</div>' +
      '</div>'
    );
  }

  /* ─── Quick-add depuis le panier ─────────────────────────────────────────── */

  function addToCart(btn) {
    const variantId = btn.dataset.variantId;
    btn.disabled    = true;
    btn.textContent = '...';

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Ajout échoué');
        btn.textContent = '✓ Ajouté';
        refreshCartSections();
      })
      .catch(function () {
        btn.textContent = '+ Ajouter';
        btn.disabled    = false;
      });
  }

  function refreshCartSections() {
    fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        const parser = new DOMParser();

        // Bubble de la navbar
        if (sections['cart-icon-bubble']) {
          const newDoc   = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          const newBubble = newDoc.querySelector('.cart-count-bubble');
          document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
            if (newBubble) el.innerHTML = newBubble.innerHTML;
          });
        }

        // Mise à jour des éléments du drawer
        if (sections['cart-drawer']) {
          const newDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const toUpdate = [
            '#CartDrawer-CartItems',
            '.cart-free-shipping',
            '.drawer__heading',
            '.totals',
            '.cart__ctas',
          ];
          toUpdate.forEach(function (sel) {
            const newEl = newDoc.querySelector(sel);
            const oldEl = document.querySelector(sel);
            if (newEl && oldEl) oldEl.outerHTML = newEl.outerHTML;
          });

          // Réinitialiser les recommandations si le produit a changé
          const newReco   = newDoc.getElementById('cart-reco');
          const oldReco   = document.getElementById('cart-reco');
          if (newReco && oldReco) {
            const newId = newReco.dataset.productId;
            if (newId !== lastProductId) {
              lastProductId = null; // force re-fetch
              oldReco.dataset.productId        = newId;
              oldReco.dataset.collectionHandle = newReco.dataset.collectionHandle || '';
              initRecommendations();
            }
          }
        }
      })
      .catch(console.error);
  }

  /* ─── Carousel ───────────────────────────────────────────────────────────── */

  function initCarousel() {
    const track = document.getElementById('cart-reco-track');
    const prev  = document.querySelector('.cart-reco-prev');
    const next  = document.querySelector('.cart-reco-next');
    if (!track || !prev || !next) return;

    // Largeur d'un item (14rem) + gap (1rem) ≈ 150px
    const scrollAmount = 150 * 2;

    // Supprimer les anciens listeners pour éviter les doublons
    const newPrev = prev.cloneNode(true);
    const newNext = next.cloneNode(true);
    prev.parentNode.replaceChild(newPrev, prev);
    next.parentNode.replaceChild(newNext, next);

    newPrev.addEventListener('click', function () {
      track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });
    newNext.addEventListener('click', function () {
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
  }

  /* ─── MutationObserver (écoute les mises à jour du panier) ──────────────── */

  function observeCart() {
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl) return;

    new MutationObserver(function (mutations) {
      const relevant = mutations.some(function (m) {
        if (m.type !== 'childList' || m.addedNodes.length === 0) return false;
        // Ignorer les changements à l'intérieur du bloc recommandations lui-même
        const inReco = m.target.closest && m.target.closest('#cart-reco');
        return !inReco;
      });
      if (relevant) initRecommendations();
    }).observe(cartDrawerEl, { childList: true, subtree: true });
  }

  /* ─── Démarrage ──────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initRecommendations();
      observeCart();
    });
  } else {
    initRecommendations();
    observeCart();
  }
})();
